import { getBalances } from 'data/Account/selectors'
import { getCurrencies as getAssetCurrencies } from 'data/Currencies/selectors'
import { CurrencyProduct } from 'data/Currencies/types'
import instrumentSelectors from 'data/Instrument/selectors'
import {
  CurrencyDefinitionType,
  ErrorType,
  RemoteType,
  StateType,
  WhitelistAddressType,
  WhitelistCapabilitiesType
} from 'data/types'
import { createSelector } from 'reselect'

import * as bankTransfer from './BankTransfer/selectors'
import * as paymentCurrenciesSelectors from './Currencies/selectors'
import * as eligible from './Elegible/selectors'
import methods from './Methods/selectors'
import * as payment from './Payment/selectors'
import { PaymentsStateType } from './reducers'
import * as submitDeposit from './SubmitDeposit/selectors'
import { getItems } from './Transactions/selectors'
import * as transactions from './Transactions/selectors'
import { Transaction, TransactionState, TransactionType } from './Transactions/types'
import { AccountType, BeneficiaryType } from './types'
import * as withdraw from './Withdrawal/selectors'
import * as withdrawFeesAndMin from './WithdrawFeesAndMin/selectors'

const getPayments = (state: StateType): PaymentsStateType => state.payments

const getAccounts: (state: StateType) => { [currency: string]: RemoteType<AccountType> } =
  createSelector(getPayments, ({ accounts }) => accounts)

const getAccount: (state: StateType, currency: string) => RemoteType<AccountType> | undefined = (
  state,
  currency
) => {
  const accounts = getAccounts(state)
  return accounts && accounts[currency]
}

type IndexedCurrenciesType = {
  [currency: string]: CurrencyDefinitionType
}
const getBeneficiariesActiveFiat: (state: StateType) => BeneficiaryType[] = createSelector<
  StateType,
  PaymentsStateType,
  IndexedCurrenciesType,
  BeneficiaryType[]
>(getPayments, instrumentSelectors.getCurrenciesIndexed, ({ beneficiaries }, currencies) =>
  beneficiaries.filter((bene) => currencies[bene.currency]?.fiat && bene.state === 'ACTIVE')
)

const getBeneficiaries: (state: StateType) => BeneficiaryType[] = createSelector(
  getPayments,
  ({ beneficiaries }) => beneficiaries
)
const getBeneficiariesLoading: (state: StateType) => boolean = createSelector(
  getPayments,
  ({ beneficiariesLoading }) => beneficiariesLoading
)
const getBeneficiariesError: (state: StateType) => ErrorType | undefined = createSelector(
  getPayments,
  ({ beneficiariesError }) => beneficiariesError
)

const getDeposits = (state: StateType) => getItems(state, TransactionType.DEPOSIT)

const getWithdrawals = (state: StateType) => getItems(state, TransactionType.WITHDRAWAL)

const getWhitelistCapabilities: (state: StateType) => WhitelistCapabilitiesType[] = createSelector<
  StateType,
  PaymentsStateType,
  IndexedCurrenciesType,
  WhitelistCapabilitiesType[]
>(getPayments, instrumentSelectors.getCurrenciesIndexed, ({ whitelistCapabilities }, currencies) =>
  whitelistCapabilities
    .filter((c) => c.fiat === false)
    .map((capa) => ({
      ...capa,
      text: currencies[capa.currency].name,
      value: capa.currency
    }))
)

const getWhitelistAddresses: (state: StateType) => WhitelistAddressType[] = createSelector(
  getPayments,
  ({ whitelistAddresses }) => whitelistAddresses.filter((addr) => addr.fiat === false)
)

const hasWhitelistedAddresses: (state: StateType) => boolean = createSelector(
  getWhitelistAddresses,
  (whitelistAddresses) => whitelistAddresses.length > 0
)

const getWhitelistAddressesOptions: (state: StateType) => WhitelistAddressType[] = createSelector(
  getWhitelistAddresses,
  (whitelistAddresses) =>
    whitelistAddresses.map((addr) => ({
      ...addr,
      text: `${addr.name} (${addr.address.slice(0, 4)} ... ${addr.address.slice(-4)})`,
      value: addr.id
    }))
)

const hasWhitelistingAddressesEnabled: (state: StateType) => boolean = createSelector(
  getPayments,
  ({ whitelistAddressesEnabled }) => whitelistAddressesEnabled
)

const getPendingDeposits = createSelector(getDeposits, (deposits) =>
  deposits.filter((d) => d.state === TransactionState.PROCESSING)
)

const totaliseTransactions = (result, payment: Transaction) => {
  const currency = payment.amount.symbol
  const amount = payment.amount.value // TODO use minor units
  result[currency] = (result[currency] ?? 0) + Number(amount)
  return result
}

const getPendingDepositsGroupedAndTotalled = createSelector(getPendingDeposits, (deposits) =>
  deposits.reduce(totaliseTransactions, {})
)

const getPendingWithdrawals = createSelector(getWithdrawals, (withdrawals) =>
  withdrawals.filter((d) => d.state === TransactionState.PROCESSING)
)

const getPendingWithdrawalsGroupedAndTotalled = createSelector(
  getPendingWithdrawals,
  (withdrawals) => withdrawals.reduce(totaliseTransactions, {})
)

const getPaymentCurrencies = (product: CurrencyProduct) =>
  createSelector(
    getAssetCurrencies,
    instrumentSelectors.getRawCurrencies,
    paymentCurrenciesSelectors.getCurrencies,
    (allCurrencies, instrumentCurrencies, paymentCurrencies) =>
      allCurrencies
        .filter((c) => c.products.includes(product))
        .filter((c) => paymentCurrencies.includes(c.symbol))
        .filter((c) => instrumentCurrencies.some((ic) => ic.key === c.symbol && !ic.blocked))
  )

const getDepositCurrencies = getPaymentCurrencies(CurrencyProduct.MercuryDeposits)

const getWithdrawCurrencies = createSelector(
  getPaymentCurrencies(CurrencyProduct.MercuryWithdrawals),
  getBalances,
  (currencies, balances) =>
    currencies.filter((cur) => balances && Number(balances[cur.symbol]?.withdrawable) > 0)
)

export default {
  bankTransfer,
  currencies: paymentCurrenciesSelectors,
  eligible,
  getAccount,
  getAccounts,
  getBeneficiaries,
  getBeneficiariesActiveFiat,
  getBeneficiariesError,
  getBeneficiariesLoading,
  getDepositCurrencies,
  getDeposits,
  getPendingDepositsGroupedAndTotalled,
  getPendingWithdrawalsGroupedAndTotalled,
  getWhitelistAddresses,
  getWhitelistAddressesOptions,
  getWhitelistCapabilities,
  getWithdrawCurrencies,
  getWithdrawals,
  hasWhitelistedAddresses,
  hasWhitelistingAddressesEnabled,
  methods,
  payment,
  submitDeposit,
  transactions,
  withdraw,
  withdrawFeesAndMin
}
